import {
	Card, Contract, PlayState, Seat, SEATS, applyPlay, dealBoard,
	legalCards, playState, remainingCards, trickWinner
} from "@ravalmatic/bridge-engine/core";
import { BoardPhase, BoardState, ContractOutcome, GameType } from "../../Types";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { BiddingManager, BiddingSnapshot } from "./BiddingManager";
import { createDevelopmentContract, DevelopmentSeats } from "./development/DevelopmentContract";
import { ActionScheduler } from "./ActionScheduler";
import { getHandController, isLocalPlayerSeat } from "./PlayerSeats";
import { chooseSampledCard } from "./BotPlayStrategy";
import type { BotPlayStrategy } from "./BotPlayStrategy";
import type { TransitionRequest, TrickCollectionRequest } from "./BoardAnimations";
import { MatchManager } from "./MatchManager";

export type ContractPresentation =
	| { kind: "contract" | "development-contract"; contract: Contract }
	| { kind: "passed-out"; contract: null };

export type ContractResultData = {
	readonly contract: Contract;
	readonly outcome: ContractOutcome;
	readonly declarerTricks: number;
};

export type BoardPhaseSnapshot = {
	phase: BoardPhase;
	contract: Contract | null;
	presentation: ContractPresentation | null;
	transition: TransitionRequest | null;
	contractResult: ContractResultData | null;
};

type BoardPhaseListener = (snapshot: BoardPhaseSnapshot) => void;

export type BotPlayError = {
	readonly seat: Seat;
	readonly message: string;
};

export type BoardPlaySnapshot = {
	state: PlayState | null;
	hands: Card[][];
	legalCards: Card[];
	isPlayerTurn: boolean;
	trickCollection: TrickCollectionRequest | null;
	botError: BotPlayError | null;
	contractResult: ContractResultData | null;
};

type BoardPlayListener = (snapshot: BoardPlaySnapshot) => void;

export class BoardManager {

	public readonly matchManager: MatchManager;
	private static readonly BOT_PLAY_DELAY = 2000;
	private static readonly BOT_LAST_CARD_DELAY = 500;

	private readonly state: Omit<BoardState, "calls">;
	private readonly biddingManager: BiddingManager;
	private readonly unsubscribeFromBidding: () => void;
	private readonly phaseListeners = new Set<BoardPhaseListener>();
	private readonly playListeners = new Set<BoardPlayListener>();
	private readonly actionScheduler = new ActionScheduler();
	private readonly animationToken = Symbol("board animations");
	private phase = BoardPhase.BIDDING;
	private contract: Contract | null = null;
	private presentation: ContractPresentation | null = null;
	private destroyed = false;
	private collectedTricks = 0;
	private botError: BotPlayError | null = null;
	private contractResult: ContractResultData | null = null;
	private readonly developmentSeats?: DevelopmentSeats;

	constructor(
		boardNumber = 1,
		developmentSeats?: DevelopmentSeats,
		private readonly botPlayStrategy: BotPlayStrategy = chooseSampledCard
	) {
        
		const seed = Math.random().toString(36).substring(2);
		this.matchManager = new MatchManager(GameVars.matchState, GameVars.gameData.gameType);

        console.log("Generated seed:", seed);

		this.state = {
			seed,
			boardNumber,
			deal: dealBoard(seed, boardNumber),
			plays: [],
		};
		if (GameVars.gameData.gameType === GameType.RUBBER) {
			this.state.deal.vul = {
				ns: GameVars.matchState.northSouth.vulnerable,
				ew: GameVars.matchState.eastWest.vulnerable
			};
		}
		if (GameConstants.DEVELOPMENT && developmentSeats
			&& SEATS.includes(developmentSeats.dealer) && SEATS.includes(developmentSeats.declarer)) {
			this.developmentSeats = { ...developmentSeats };
			this.state.deal.dealer = developmentSeats.dealer;
		}

		this.biddingManager = new BiddingManager(
			this.state.deal,
			this.state.seed,
			this.state.boardNumber
		);
		this.unsubscribeFromBidding = this.biddingManager.subscribe(this.onBiddingStateChanged.bind(this));
	}

	public subscribeToPhase(listener: BoardPhaseListener): () => void {

		this.phaseListeners.add(listener);
		listener(this.getPhaseSnapshot());
		return () => this.phaseListeners.delete(listener);
	}

	public getPhaseSnapshot(): BoardPhaseSnapshot {

		const presentation: ContractPresentation | null = this.presentation === null
			? null
			: this.presentation.kind === "passed-out"
				? { kind: "passed-out", contract: null }
				: { kind: this.presentation.kind, contract: { ...this.presentation.contract } };
		return {
			phase: this.phase,
			contract: this.contract ? { ...this.contract } : null,
			presentation,
			transition: this.phase === BoardPhase.TRANSITION
				? { kind: "transition", token: this.animationToken } : null,
			contractResult: this.contractResult
				? { ...this.contractResult, contract: { ...this.contractResult.contract } } : null
		};
	}

	public requestContinue(): boolean {

		if (this.destroyed || this.phase !== BoardPhase.CONTRACT) {
			console.warn(`Cannot continue bidding from phase ${this.phase}`);
			return false;
		}

		this.setPhase(BoardPhase.TRANSITION);
		return true;
	}

	public completeTransition(request: TransitionRequest): boolean {

		if (this.destroyed || this.phase !== BoardPhase.TRANSITION
			|| request?.kind !== "transition" || request.token !== this.animationToken) {
			console.warn(`Cannot complete board transition from phase ${this.phase}`);
			return false;
		}

		if (!this.contract) {
			this.matchManager.recordHand(this.state.boardNumber, this.state.deal.dealer, null, 0);
		}
		this.setPhase(this.contract ? BoardPhase.PLAY : BoardPhase.FINISHED);
		if (this.contract) {
			this.advancePlay();
		}
		return true;
	}

	public canPlay(): boolean {

		const snapshot = this.getPlaySnapshot();
		return this.phase === BoardPhase.PLAY && !this.isPlayFrozen() && snapshot.state !== null && !snapshot.state.isOver
			&& snapshot.trickCollection === null && snapshot.botError === null;
	}

	public subscribeToPlay(listener: BoardPlayListener): () => void {

		this.playListeners.add(listener);
		listener(this.getPlaySnapshot());
		return () => this.playListeners.delete(listener);
	}

	public getPlaySnapshot(): BoardPlaySnapshot {

		const frozen = this.isPlayFrozen();
		const stopped = this.contractResult !== null;
		const play = !this.destroyed && (this.phase === BoardPhase.PLAY || this.phase === BoardPhase.RESULT) && this.contract
			? playState(this.state.deal, { ...this.contract }, [...this.state.plays])
			: null;
		const state = frozen && play ? { ...play, dummyVisible: true } : play;
		const completedTrick = state?.completedTricks[this.collectedTricks];
		const trickCollection: TrickCollectionRequest | null = !frozen && !stopped && state && completedTrick
			? {
				kind: "trick-collection",
				token: this.animationToken,
				trickNumber: this.collectedTricks + 1,
				winner: trickWinner(completedTrick, state.contract.strain)
			} : null;
		return {
			state,
			hands: SEATS.map(seat => remainingCards(this.state.deal, this.state.plays, seat)),
			legalCards: !frozen && !stopped && state && !trickCollection && !this.botError ? legalCards(this.state.deal, state) : [],
			isPlayerTurn: !frozen && !stopped && state !== null && !state.isOver && trickCollection === null
				&& this.botError === null && isLocalPlayerSeat(getHandController(state)),
			trickCollection,
			botError: this.botError ? { ...this.botError } : null,
			contractResult: this.contractResult
				? { ...this.contractResult, contract: { ...this.contractResult.contract } } : null
		};
	}

	public forceContractResult(outcome: ContractOutcome): boolean {

		if (!GameConstants.DEVELOPMENT || this.destroyed || this.phase !== BoardPhase.PLAY
			|| !this.contract || (outcome !== "made" && outcome !== "defeated")) {
			console.warn(`Cannot force contract result ${outcome} from phase ${this.phase}`);
			return false;
		}
		this.actionScheduler.cancel();
		this.botError = null;
		this.contractResult = {
			contract: { ...this.contract },
			outcome,
			declarerTricks: this.contract.level + 6 - (outcome === "made" ? 0 : 2)
		};
		this.matchManager.recordHand(
			this.state.boardNumber, this.state.deal.dealer,
			this.contractResult.contract, this.contractResult.declarerTricks,
			this.matchManager.gameType === GameType.RUBBER ? undefined : {
				northSouth: Phaser.Math.Between(5, 50) * 10, eastWest: Phaser.Math.Between(5, 50) * 10
			}
		);
		this.setPhase(BoardPhase.RESULT);
		this.advancePlay();
		return true;
	}

	public completeContractResult(): boolean {

		if (this.destroyed || this.phase !== BoardPhase.RESULT || !this.contractResult) {
			console.warn(`Cannot continue contract result from phase ${this.phase}`);
			return false;
		}
		this.setPhase(BoardPhase.FINISHED);
		this.advancePlay();
		return true;
	}

	public retryBotPlay(): boolean {

		const snapshot = this.getPlaySnapshot();
		if (this.destroyed || this.phase !== BoardPhase.PLAY || this.botError === null
			|| this.isPlayFrozen() || snapshot.state === null || snapshot.state.isOver
			|| snapshot.trickCollection !== null || isLocalPlayerSeat(getHandController(snapshot.state))) {
			console.warn(`Cannot retry automatic play from phase ${this.phase}`);
			return false;
		}
		this.botError = null;
		this.advancePlay();
		return true;
	}

	public completeTrickCollection(request: TrickCollectionRequest): boolean {

		const snapshot = this.getPlaySnapshot();
		const pending = snapshot.trickCollection;
		if (this.destroyed || this.phase !== BoardPhase.PLAY
			|| request?.kind !== "trick-collection" || request.token !== this.animationToken
			|| pending === null || pending.trickNumber !== request.trickNumber
			|| pending.winner !== request.winner) {
			console.warn(`Cannot complete trick collection ${request?.trickNumber} from phase ${this.phase}`);
			return false;
		}
		this.collectedTricks = request.trickNumber;
		this.advancePlay();
		return true;
	}

	private isPlayFrozen(): boolean {

		return GameConstants.DEVELOPMENT && GameConstants.DEVELOPMENT_FREEZE_PLAY;
	}

	public submitPlayerCard(seat: Seat, card: Card): boolean {

		const snapshot = this.getPlaySnapshot();
		if (!snapshot.isPlayerTurn || snapshot.state?.handToPlay !== seat
			|| !snapshot.legalCards.includes(card)) {
			console.warn(`Cannot play card ${card} from seat ${seat}`);
			return false;
		}
		this.state.plays = applyPlay(this.state.deal, snapshot.state, card);
		this.advancePlay();
		return true;
	}

	public destroy(): void {

		this.destroyed = true;
		this.actionScheduler.destroy();
		this.unsubscribeFromBidding();
		this.phaseListeners.clear();
		this.playListeners.clear();
		this.biddingManager.stop();
	}

	private advancePlay(): void {

		this.actionScheduler.cancel();
		const snapshot = this.getPlaySnapshot();
		this.playListeners.forEach(listener => listener(snapshot));
		if (this.destroyed || this.phase !== BoardPhase.PLAY || snapshot.state === null
			|| this.isPlayFrozen() || snapshot.trickCollection !== null || snapshot.botError !== null) {
			return;
		}
		if (snapshot.state.isOver) {
			const requiredTricks = snapshot.state.contract.level + 6;
			this.contractResult = {
				contract: { ...snapshot.state.contract },
				outcome: snapshot.state.declarerTricks >= requiredTricks ? "made" : "defeated",
				declarerTricks: snapshot.state.declarerTricks
			};
			this.matchManager.recordHand(
				this.state.boardNumber, this.state.deal.dealer,
				this.contractResult.contract, this.contractResult.declarerTricks
			);
			this.setPhase(BoardPhase.RESULT);
		} else if (!snapshot.isPlayerTurn) {
			const seat = snapshot.state.handToPlay;
			const delay = snapshot.hands[seat].length === 1
				? BoardManager.BOT_LAST_CARD_DELAY : BoardManager.BOT_PLAY_DELAY;
			this.actionScheduler.schedule(delay, async isCurrent => {
				const current = this.getPlaySnapshot();
				if (!this.canPlayAutomatically(current)) {
					return;
				}
				const board = this.getState();
				const card = await this.botPlayStrategy({
					deal: board.deal,
					state: current.state,
					legalCards: current.legalCards,
					calls: board.calls,
					seed: board.seed,
					boardNumber: board.boardNumber,
					difficulty: GameVars.gameData.difficulty
				});
				if (!isCurrent()) {
					return;
				}
				const latest = this.getPlaySnapshot();
				if (!this.canPlayAutomatically(latest)) {
					return;
				}
				if (!latest.legalCards.includes(card)) {
					throw new Error(`Automatic player chose illegal card ${card} from seat ${latest.state.handToPlay}`);
				}
				this.state.plays = applyPlay(this.state.deal, latest.state, card);
				this.advancePlay();
			}, error => {
				console.error("Unable to choose bot card", error);
				this.botError = {
					seat,
					message: error instanceof Error ? error.message : String(error)
				};
				this.advancePlay();
			});
		}
	}

	private canPlayAutomatically(snapshot: BoardPlaySnapshot): snapshot is BoardPlaySnapshot & { state: PlayState } {

		return !this.destroyed && this.phase === BoardPhase.PLAY && !this.isPlayFrozen()
			&& snapshot.state !== null && !snapshot.state.isOver
			&& !snapshot.isPlayerTurn && snapshot.trickCollection === null && snapshot.botError === null;
	}

	private onBiddingStateChanged(snapshot: BiddingSnapshot): void {

		if (this.phase !== BoardPhase.BIDDING || !snapshot.state.isOver) {
			return;
		}

		const useDevelopmentContract = GameConstants.DEVELOPMENT && snapshot.isForcedFinish;
		this.contract = useDevelopmentContract
			? createDevelopmentContract(this.developmentSeats?.declarer) : snapshot.contract;
		this.presentation = this.contract
			? {
				kind: useDevelopmentContract ? "development-contract" : "contract",
				contract: this.contract
			}
			: { kind: "passed-out", contract: null };
		this.setPhase(BoardPhase.CONTRACT);
	}

	private setPhase(phase: BoardPhase): void {

		this.phase = phase;
		const snapshot = this.getPhaseSnapshot();
		this.phaseListeners.forEach(listener => listener(snapshot));
	}

	public getBiddingManager(): BiddingManager {

		return this.biddingManager;
	}

	public getState(): BoardState {

		return {
			seed: this.state.seed,
			boardNumber: this.state.boardNumber,
			deal: {
				hands: this.state.deal.hands.map(hand => [...hand]),
				dealer: this.state.deal.dealer,
				vul: { ...this.state.deal.vul },
			},
			calls: [...this.biddingManager.getCalls()],
			plays: [...this.state.plays],
		};
	}
}
